import React, { useState } from "react";
import { InfoBanner, Spinner, TableHeader, WithModulesAccess } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { AllModulesTagName } from "../../../utils/enums";
import { useGetOneTechMapQuery } from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import ErrorService from "../../../services/ErrorService";
import ProductsForTechMapTable from "./ProductsForTechMapTable";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    setStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import WarehouseJoyrideWrapper from "../../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import { useCheckServicesLazyQuery } from "@me-team/host/main/tutorial/graphql/tutorial.hooks";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const ProductsForTechMap: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canOpenTechMapServices = useRoleAccess(CompanyAccess.TechMapServiceListView);
    const canEditTechMapProducts = useRoleAccess(CompanyAccess.TechMapProductListEdit);
    const { techMapId, branchId } = useParams();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();

    const [checkServices, { loading: checkServicesLoading }] = useCheckServicesLazyQuery();

    const handleNextStepTutorial = async () => {
        if (!canOpenTechMapServices) return;

        const { data: servicesData } =
            tutorialActive &&
            currentCompanyId &&
            (await checkServices({
                variables: { companyId: Number(currentCompanyId) },
            }));

        const isServices = !!servicesData?.user?.company?.servicesList?.length;
        if (servicesData) {
            if (isServices && tutorialActive) {
                setStepInLocalStorage(15);
                window.dispatchEvent(new Event("warehouseStepChanged"));
            } else if (tutorialActive) {
                moveStepInLocalStorage(StepDirection.NEXT);
            }
        }
    };

    const { data: oneTechMapData, loading: oneTechMapLoading } = useGetOneTechMapQuery({
        variables: {
            techMapId: +techMapId,
            companyId: +currentCompanyId,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const techMapName: string = oneTechMapData?.user?.company?.technologicalMaps?.technologicalMaps?.[0].name;

    if (oneTechMapLoading || !currentCompanyId || checkServicesLoading) return <Spinner />;

    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t("Add products to the card, specify quantity via")}
                    <i className="bi bi-pencil text-dark px-1"></i>
                    {t("“Edit”. Products will be written off after the related service.")}
                </p>
            </InfoBanner>
            <TableHeader
                title={`${t("Products")} ${techMapName}`}
                paths={[
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/products/branch/${branchId}`,
                        label: t("Warehouse accounting"),
                    },
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/branch/${branchId}`,
                        label: t("List of" + " Technological Maps"),
                    },
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/products/branch/${branchId}`,
                        label: `${t("Products")} ${techMapName}`,
                    },
                ]}
                colSize={6}
                btnColSize={6}
                buttons={
                    <Row className="mt-4 mt-lg-0 gx-3 justify-content-end">
                        <WithModulesAccess modules={[AllModulesTagName.SERVICES]}>
                            {canOpenTechMapServices && (
                                <Col xs={12} md={6} xxl={5} className="mb-3 mb-md-0">
                                    <Link
                                        to={`/warehouse/technological-maps/${currentCompanyId}/${techMapId}/services/branch/${branchId}`}
                                        className="btn btn-outline-orange px-2 w-100 text-truncate text-capitalize"
                                        onClick={handleNextStepTutorial}
                                        data-tour="to-services-tech-map-btn"
                                    >
                                        {t("To Services tech cards")}
                                    </Link>
                                </Col>
                            )}
                        </WithModulesAccess>
                        {canEditTechMapProducts ? (
                            <Col xs={12} md={6} xxl={7}>
                                <Button
                                    variant="primary"
                                    className="w-100 text-white mb-3 mb-lg-0 text-truncate"
                                    onClick={() => {
                                        if (!canEditTechMapProducts) return;

                                        navigate(
                                            `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/product/add/branch/${branchId}`
                                        );
                                    }}
                                >
                                    <i className="bi bi-plus-lg fw-bold me-1 w-100 h-100"></i>
                                    {t("Add Product to Technological Map")}
                                </Button>
                            </Col>
                        ) : null}
                    </Row>
                }
                showFilters={false}
            ></TableHeader>
            <ProductsForTechMapTable />

            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper tutorialType={tutorialType} startStep={startStep} />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default ProductsForTechMap;
